import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, AlertCircle, Loader2 } from 'lucide-react'
import { Modal, Button, Alert, Input, Select, Radio } from '@components/common'
import { userService } from '@services/userService'
import toast from 'react-hot-toast'
import './TeamMemberForm.scss'

/**
 * Form Validation Schema (Base)
 */
const baseSchema = z.object({
	role_id: z.number().positive('Role is required'),
	first_name: z.string().min(2, 'First name must be at least 2 characters').max(100),
	last_name: z.string().min(2, 'Last name must be at least 2 characters').max(100),
	email: z.string().email('Invalid email address'),
	phone: z.string().optional(),
	status: z.enum(['active', 'inactive', 'suspended'], {
		errorMap: () => ({ message: 'Status must be active, inactive, or suspended' }),
	}),
})

/**
 * Create Schema - Password REQUIRED
 */
const createSchema = baseSchema.extend({
	password: z.string().min(8, 'Password must be at least 8 characters'),
})

/**
 * Edit Schema - Password OPTIONAL
 */
const editSchema = baseSchema.extend({
	password: z.string().optional(),
})

/**
 * Team Member Form Component
 *
 * Form for creating and editing team members.
 * Roles are pre-filtered to exclude super-admin.
 *
 * @param {string} mode - 'create' or 'edit'
 * @param {Array} roles - Available roles (already filtered)
 * @param {Object} user - User data for edit mode
 * @param {Function} onClose - Close modal callback
 * @param {Function} onSuccess - Success callback
 *
 * NOTE: company_id is automatically set by backend from authenticated user
 */
const TeamMemberForm = ({ mode = 'create', roles = [], user = null, onClose, onSuccess }) => {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState(null)

	const isEditMode = mode === 'edit'

	// Select the appropriate schema based on mode
	const validationSchema = isEditMode ? editSchema : createSchema

	// Filter active roles only
	const activeRoles = roles.filter((role) => role.status === 'active')

	// Initialize form with React Hook Form + Zod
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors, isDirty },
	} = useForm({
		resolver: zodResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			role_id: user?.role_id ? Number(user.role_id) : 0,
			first_name: user?.first_name || '',
			last_name: user?.last_name || '',
			email: user?.email || '',
			phone: user?.phone || '',
			password: '',
			status: user?.status || 'active',
		},
	})

	// Watch status value for radio component
	const statusValue = watch('status')

	// Handle status change
	const handleStatusChange = (e) => {
		setValue('status', e.target.value, { shouldValidate: true, shouldDirty: true })
	}

	// Handle form submission
	const onSubmit = async (data) => {
		setIsSubmitting(true)
		setSubmitError(null)

		try {
			// SECURITY: company_id is automatically set by backend from authenticated user
			// No need to pass it from frontend
			const formData = {
				role_id: Number(data.role_id),
				first_name: data.first_name,
				last_name: data.last_name,
				email: data.email,
				phone: data.phone || '',
				status: data.status,
			}

			// Only include password in create mode
			if (!isEditMode && data.password) {
				formData.password = data.password
			}

			if (isEditMode && user) {
				await userService.update(user.id, formData)
				toast.success('Team member updated successfully!')
			} else {
				await userService.create(formData)
				toast.success('Team member created successfully!')
			}

			onSuccess?.()
		} catch (error) {
			console.error('Form submission error:', error)

			// Extract error message
			let errorMessage = 'Failed to save team member'
			if (error.response?.data) {
				const responseData = error.response.data
				if (responseData.messages) {
					if (typeof responseData.messages === 'object') {
						const errors = Object.values(responseData.messages).filter((msg) => typeof msg === 'string')
						if (errors.length > 0) {
							errorMessage = errors.join(', ')
						}
					} else if (typeof responseData.messages === 'string') {
						errorMessage = responseData.messages
					}
				} else if (responseData.message) {
					errorMessage = responseData.message
				}
			} else if (error.message) {
				errorMessage = error.message
			}

			setSubmitError(errorMessage)
		} finally {
			setIsSubmitting(false)
		}
	}

	const modalTitle = isEditMode ? 'Edit Team Member' : 'Add New Team Member'

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
				Cancel
			</Button>
			<Button variant="primary" onClick={handleSubmit(onSubmit)} loading={isSubmitting} disabled={!isDirty && isEditMode}>
				{isSubmitting ? (
					<>
						<Loader2 className="spin" size={18} />
						<span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
					</>
				) : (
					<>
						<Save size={18} />
						<span>{isEditMode ? 'Update Member' : 'Add Member'}</span>
					</>
				)}
			</Button>
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title={modalTitle}
			size="large"
			footer={footer}
			closeOnEscape={false}
			closeOnBackdrop={false}
		>
			<form className="team-member-form" onSubmit={handleSubmit(onSubmit)}>
				{submitError && <Alert variant="error" message={submitError} dismissible onClose={() => setSubmitError(null)} />}

				{/* Role Selection */}
				<div className="team-member-form__section">
					<h3 className="team-member-form__section-title">Role Assignment</h3>

					<div className="form-group">
						<Select
							label="Role"
							placeholder="Select Role"
							required
							options={[
								{ value: 0, label: 'Select Role' },
								...activeRoles.map((role) => ({
									value: role.id,
									label: role.name,
								})),
							]}
							error={errors.role_id?.message}
							disabled={isSubmitting}
							{...register('role_id', { valueAsNumber: true })}
						/>
						<p className="form-hint">Select the role that defines the member's permissions and access level.</p>
					</div>
				</div>

				{/* Personal Information */}
				<div className="team-member-form__section">
					<h3 className="team-member-form__section-title">Personal Information</h3>

					<div className="team-member-form__grid">
						<div className="form-group">
							<Input
								type="text"
								label="First Name"
								placeholder="e.g., John"
								required
								error={errors.first_name?.message}
								disabled={isSubmitting}
								{...register('first_name')}
							/>
						</div>

						<div className="form-group">
							<Input
								type="text"
								label="Last Name"
								placeholder="e.g., Doe"
								required
								error={errors.last_name?.message}
								disabled={isSubmitting}
								{...register('last_name')}
							/>
						</div>
					</div>

					<div className="team-member-form__grid">
						<div className="form-group">
							<Input
								type="email"
								label="Email"
								placeholder="e.g., john.doe@company.com"
								required
								error={errors.email?.message}
								disabled={isSubmitting}
								{...register('email')}
							/>
						</div>

						<div className="form-group">
							<Input
								type="tel"
								label="Phone"
								placeholder="e.g., (555) 123-4567"
								error={errors.phone?.message}
								disabled={isSubmitting}
								{...register('phone')}
							/>
						</div>
					</div>
				</div>

				{/* Security */}
				<div className="team-member-form__section">
					<h3 className="team-member-form__section-title">Security</h3>

					<div className="team-member-form__grid">
						{!isEditMode && (
							<div className="form-group">
								<Input
									type="password"
									label="Password"
									placeholder="Minimum 8 characters"
									required
									error={errors.password?.message}
									disabled={isSubmitting}
									{...register('password')}
								/>
							</div>
						)}

						{isEditMode && (
							<div className="form-group">
								<label className="form-label">Password</label>
								<div className="form-hint-box">
									<AlertCircle size={16} />
									<span>Use "Reset Password" action to change user password</span>
								</div>
							</div>
						)}

						<div className="form-group">
							<Radio
								label="Status"
								name="status"
								value={statusValue}
								onChange={handleStatusChange}
								required
								options={[
									{ value: 'active', label: 'Active' },
									{ value: 'inactive', label: 'Inactive' },
									{ value: 'suspended', label: 'Suspended' },
								]}
								error={errors.status?.message}
								disabled={isSubmitting}
							/>
						</div>
					</div>
				</div>
			</form>
		</Modal>
	)
}

TeamMemberForm.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	roles: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
			status: PropTypes.string,
		})
	),
	user: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
}

export default TeamMemberForm
